import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  ClockIcon,
  DocumentTextIcon,
  BanknotesIcon,
  FolderIcon
} from '@heroicons/react/24/outline'
import { crmAPI, CustomerStats, CustomerTimeline } from '@/services/api'

export default function CRMCustomerDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [stats, setStats] = useState<CustomerStats | null>(null)
  const [timeline, setTimeline] = useState<CustomerTimeline[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'bills'>('overview')

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return

      try {
        setLoading(true)
        const [statsData, timelineData] = await Promise.all([
          crmAPI.getCustomerStats(parseInt(id)),
          crmAPI.getCustomerTimeline(parseInt(id), 50)
        ])
        setStats(statsData)
        setTimeline(timelineData.timeline)
        setError(null)
      } catch (err: any) {
        console.error('Error fetching customer:', err)
        setError(err.message || 'Erro ao carregar cliente')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Erro: {error || 'Cliente não encontrado'}</p>
          <button
            onClick={() => navigate('/crm/customers')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Voltar
          </button>
        </div>
      </div>
    )
  }

  const { customer } = stats

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/crm/customers')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                {customer.name}
                {customer.is_vip && (
                  <span className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                    VIP
                  </span>
                )}
                {customer.is_at_risk && (
                  <span className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-full">
                    Em Risco
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-1">
                {customer.company || customer.email} • {customer.business_roles.join(', ')}
              </p>
            </div>
          </div>

          <button
            onClick={() => crmAPI.recalculateCustomerMetrics(parseInt(id!))}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Recalcular Métricas
          </button>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Lifetime Value</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(stats.metrics.lifetime_value)}
                </p>
              </div>
              <CurrencyDollarIcon className="w-10 h-10 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Health Score</p>
                <p className="text-2xl font-bold text-green-600">
                  {Math.round(stats.metrics.health_score)}
                </p>
              </div>
              <HeartIcon className="w-10 h-10 text-green-500" />
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-green-600"
                style={{ width: `${stats.metrics.health_score}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pedidos</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.metrics.total_orders}
                </p>
              </div>
              <ChartBarIcon className="w-10 h-10 text-purple-500" />
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Ticket Médio: {formatCurrency(stats.metrics.average_ticket)}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Risk Score</p>
                <p className="text-2xl font-bold text-red-600">
                  {Math.round(stats.metrics.risk_score)}
                </p>
              </div>
              <ExclamationTriangleIcon className="w-10 h-10 text-red-500" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Visão Geral
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'timeline'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() => setActiveTab('bills')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'bills'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Financeiro
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Ranking Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Ranking & Papéis</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                        <span className="text-gray-700">Customer Rank</span>
                        <span className="font-bold text-blue-600">
                          {stats.ranking.customer_rank}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                        <span className="text-gray-700">Supplier Rank</span>
                        <span className="font-bold text-orange-600">
                          {stats.ranking.supplier_rank}
                        </span>
                      </div>
                      <div className="flex gap-2 mt-4">
                        {stats.ranking.business_roles.map((role) => (
                          <span
                            key={role}
                            className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Bills Info */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Financeiro</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="text-gray-700">Contas Abertas</span>
                        <span className="font-bold text-green-600">
                          {stats.bills.count_open} ({formatCurrency(stats.bills.total_open)})
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <span className="text-gray-700">Vencidas</span>
                        <span className="font-bold text-red-600">
                          {stats.bills.count_overdue} ({formatCurrency(stats.bills.total_overdue)})
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-gray-700">Pagas</span>
                        <span className="font-bold text-gray-600">
                          {stats.bills.count_paid}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Timeline de Atividades ({timeline.length} eventos)
                </h3>
                {timeline.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nenhuma atividade registrada</p>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((event, index) => (
                      <div
                        key={index}
                        className="flex gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-shrink-0">
                          {event.type === 'bill' && (
                            <BanknotesIcon className="w-6 h-6 text-green-600" />
                          )}
                          {event.type === 'project' && (
                            <FolderIcon className="w-6 h-6 text-blue-600" />
                          )}
                          {event.type === 'interaction' && (
                            <ClockIcon className="w-6 h-6 text-purple-600" />
                          )}
                          {event.type === 'opportunity' && (
                            <ChartBarIcon className="w-6 h-6 text-orange-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{event.title}</p>
                              {event.description && (
                                <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                              )}
                            </div>
                            <span className="text-sm text-gray-500">
                              {formatDate(event.date)}
                            </span>
                          </div>
                          {event.amount && (
                            <p className="text-sm font-medium text-green-600 mt-2">
                              {formatCurrency(event.amount)}
                            </p>
                          )}
                          {event.status && (
                            <span className="inline-block mt-2 px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                              {event.status}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bills Tab */}
            {activeTab === 'bills' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Resumo Financeiro</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">Total Receita</p>
                    <p className="text-2xl font-bold text-green-900 mt-2">
                      {formatCurrency(stats.metrics.total_revenue)}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium">LTV</p>
                    <p className="text-2xl font-bold text-blue-900 mt-2">
                      {formatCurrency(stats.metrics.lifetime_value)}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                    <p className="text-sm text-purple-800 font-medium">Ticket Médio</p>
                    <p className="text-2xl font-bold text-purple-900 mt-2">
                      {formatCurrency(stats.metrics.average_ticket)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}
